import type { Editor } from "@tiptap/react";
import type { MessageInputRef } from "@v2/features/chat/components/chat-elements/message-input/MessageInput";
import type { Industry } from "./prompt-suggestions/industries";
import type {
  PromptCategory,
  PromptCategoryConfig,
} from "./prompt-suggestions/types";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import twMerge from "@replo/design-system/utils/twMerge";
import Placeholder from "@tiptap/extension-placeholder";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { HorizontalCarousel } from "@v2/components/carousel/HorizontalCarousel";
import { TutorialAnchorWrapper } from "@v2/features/activation/components/HomepageTutorial";
import PaymentFailedBanner from "@v2/features/billing/PaymentFailedBanner";
import AssetComponent from "@v2/features/chat/components/chat-elements/message-input/assetNodeExtension";
import IntegrationComponent from "@v2/features/chat/components/chat-elements/message-input/integrationNodeExtension";
import { MessageInput } from "@v2/features/chat/components/chat-elements/message-input/MessageInput";
import ProductComponent from "@v2/features/chat/components/chat-elements/message-input/productNodeExtension";
import HardBreakMarkdown from "@v2/features/chat/extensions/hardBreakMarkdown";
import useChatMessageLimits from "@v2/features/chat/hooks/useChatMessageLimits";
import { useQuickPrompt } from "@v2/features/chat/hooks/useQuickPrompt";
import { useCurrentChatContext } from "@v2/features/chat/providers/CurrentChatContext";
import {
  chatEditorHandleKeyDown,
  chatEditorHandlePaste,
  getCleanedChatPrompt,
  useChatEditorOnUpdate,
} from "@v2/features/chat/utils/tiptapUtils";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { routes } from "@v2/utils/router";
import { ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

import { PROMPT_CATEGORIES } from "./prompt-suggestions/categories";
import { PROMPTS_BY_CATEGORY } from "./prompt-suggestions/categoryPrompts";
import { INDUSTRIES, INDUSTRY_ICONS } from "./prompt-suggestions/industries";
import { INDUSTRY_PROMPTS } from "./prompt-suggestions/industryPrompts";
import { CYCLING_PLACEHOLDERS } from "./prompt-suggestions/placeholders";

// Logos to show in the integration banner (pick a diverse subset)
const INTEGRATION_BANNER_LOGOS = [
  INTEGRATION_CONFIGS.Klaviyo,
  INTEGRATION_CONFIGS.GA4,
  INTEGRATION_CONFIGS.Meta,
  INTEGRATION_CONFIGS.Hotjar,
  INTEGRATION_CONFIGS.GoogleTagManager,
  INTEGRATION_CONFIGS.TikTok,
  INTEGRATION_CONFIGS.Pinterest,
] as const;

const heroTextOptions = [
  "Build your next winning idea",
  "Create pages customers say yes to",
  "Turn ideas into revenue",
  "Bring concepts to conversion",
  "Turn traffic into customers",
  "Build landing pages that perform",
];

function ChipButton({
  onClick,
  startEnhancer,
  children,
}: React.PropsWithChildren<{
  onClick?: () => void;
  startEnhancer?: React.ReactNode;
}>) {
  return (
    <Button
      variant="secondary"
      size="base"
      startEnhancer={startEnhancer}
      onClick={onClick}
      UNSAFE_className="font-normal rounded-md"
    >
      {children}
    </Button>
  );
}

export function StartChat() {
  const { files, setFiles, addFiles } = useCurrentChatContext();
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] =
    useState<PromptCategory | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(
    null,
  );
  const [industrySearch, setIndustrySearch] = useState("");
  const [heroText] = useState(
    () => heroTextOptions[Math.floor(Math.random() * heroTextOptions.length)]!,
  );

  const homeEditorRef = useRef<Editor | null>(null);
  const messageInputRef = useRef<MessageInputRef>(null);
  const categoryChipsRef = useRef<HTMLDivElement>(null);
  const industryChipsRef = useRef<HTMLDivElement>(null);
  const [canScrollIndustryLeft, setCanScrollIndustryLeft] = useState(false);
  const [canScrollIndustryRight, setCanScrollIndustryRight] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const projectId = useCurrentProjectId();
  const logEvent = useLogAnalytics();
  const shouldShowNewIntegrationsUi = isFeatureEnabled("integrations-v2-ui");
  const shouldShowConnectToolsBanner =
    !location.pathname.startsWith("/shops/") || shouldShowNewIntegrationsUi;

  const { messagesRemaining, hasUnlimitedMessages } = useChatMessageLimits();

  const currentPrompts = selectedCategory
    ? PROMPTS_BY_CATEGORY[selectedCategory]
    : [];

  const selectedCategoryConfig = useMemo((): PromptCategoryConfig | null => {
    if (!selectedCategory) {
      return null;
    }
    return PROMPT_CATEGORIES.find((cat) => cat.id === selectedCategory) ?? null;
  }, [selectedCategory]);

  const handleClearCategory = useCallback(() => {
    setSelectedCategory(null);
    setSelectedIndustry(null);
    setIndustrySearch("");
  }, []);

  const filteredIndustries = useMemo(() => {
    if (!industrySearch.trim()) {
      return INDUSTRIES;
    }
    const search = industrySearch.toLowerCase();
    return INDUSTRIES.filter((industry) => {
      return industry.toLowerCase().includes(search);
    });
  }, [industrySearch]);

  const handleCategoryChange = useCallback((category: PromptCategory) => {
    setSelectedCategory(category);
    setIndustrySearch("");
  }, []);

  const handleBackFromIndustry = useCallback(() => {
    setSelectedCategory(null);
    setSelectedIndustry(null);
    setIndustrySearch("");
  }, []);

  const handleIndustrySelect = useCallback((industry: Industry) => {
    setSelectedIndustry(industry);
  }, []);

  const handleBackFromIndustryPrompts = useCallback(() => {
    setSelectedIndustry(null);
  }, []);

  const checkIndustryScroll = useCallback(() => {
    if (industryChipsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = industryChipsRef.current;
      setCanScrollIndustryLeft(scrollLeft > 0);
      setCanScrollIndustryRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  }, []);

  useEffect(() => {
    // Only attach listeners when industry chips are visible (by-industry selected, no specific industry yet)
    if (selectedCategory === "by-industry" && selectedIndustry === null) {
      checkIndustryScroll();
      const container = industryChipsRef.current;
      if (container) {
        container.addEventListener("scroll", checkIndustryScroll);
        window.addEventListener("resize", checkIndustryScroll);
        return () => {
          container.removeEventListener("scroll", checkIndustryScroll);
          window.removeEventListener("resize", checkIndustryScroll);
        };
      }
    }
  }, [selectedCategory, selectedIndustry, checkIndustryScroll]);

  const handleIndustrySearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIndustrySearch(e.target.value);
      // Delay scroll check to allow DOM to update
      setTimeout(checkIndustryScroll, 0);
    },
    [checkIndustryScroll],
  );

  const scrollIndustry = useCallback((direction: "left" | "right") => {
    if (industryChipsRef.current) {
      const scrollAmount = industryChipsRef.current.clientWidth;
      industryChipsRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  }, []);

  const handleSubmit = useCallback(() => {
    const editor = homeEditorRef.current;
    if (!editor || !projectId) {
      return;
    }

    if (!hasUnlimitedMessages && messagesRemaining <= 0) {
      return;
    }

    const prompt = getCleanedChatPrompt(editor);
    if (!prompt) {
      return;
    }

    editor.commands.setContent("", false);
    setFiles(null);

    // NOTE (Bobby, 2025-07-30): Storing this through react router's state so in the browser's session history. Potentially want to make this
    // in a shared context but the home and chat were just in pretty unrelated contexts so doing this for now.
    navigate(`/shops/${projectId}/chat`, {
      state: {
        initialMessage: prompt,
        initialFiles: files,
      },
    });
  }, [
    projectId,
    navigate,
    files,
    messagesRemaining,
    hasUnlimitedMessages,
    setFiles,
  ]);

  const chatEditorOnUpdate = useChatEditorOnUpdate();

  // Set up the Tiptap editor
  const homeEditor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false,
        paragraph: {
          HTMLAttributes: {
            style: "white-space: pre-wrap",
          },
        },
      }),
      AssetComponent,
      ProductComponent,
      IntegrationComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        /*
         * NOTE (Kevin, 2025-09-24):
         * breaks=false because the HardBreak extension emits <br /> directly.
         * html=true keeps <chat-token> placeholders intact through markdown.
         * tightLists=true prevents blank paragraphs between list items.
         */
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Ask Replo anything...",
      }),
    ],
    content: "",
    onUpdate: chatEditorOnUpdate,
    autofocus: true,
    editorProps: {
      attributes: {
        style: "white-space: pre-wrap;",
        class: "home-chat-input text-sm",
      },
      handleKeyDown: chatEditorHandleKeyDown(handleSubmit),
      handlePaste: chatEditorHandlePaste(addFiles),
    },
  });

  useEffect(() => {
    homeEditorRef.current = homeEditor;
  }, [homeEditor]);

  const [typedPlaceholder, setTypedPlaceholder] = useState("");
  useEffect(() => {
    let cancelled = false;
    const phrase = CYCLING_PLACEHOLDERS[placeholderIndex] ?? "";

    const TYPE_DELAY = 28;
    const DELETE_DELAY = 16;
    const START_DELAY = 250;
    const DWELL_AFTER = 3000;

    let charIndex = 0;
    let typeTimer: number | undefined;
    let dwellTimer: number | undefined;
    let deleteTimer: number | undefined;

    const typeStep = () => {
      if (cancelled) {
        return;
      }
      requestAnimationFrame(() => {
        setTypedPlaceholder(phrase.slice(0, charIndex + 1));
      });
      charIndex += 1;
      if (charIndex < phrase.length) {
        typeTimer = window.setTimeout(typeStep, TYPE_DELAY);
      } else {
        dwellTimer = window.setTimeout(deletePhase, DWELL_AFTER);
      }
    };

    const deletePhase = () => {
      if (cancelled) {
        return;
      }
      const deleteStep = () => {
        if (cancelled) {
          return;
        }
        charIndex -= 1;
        requestAnimationFrame(() => {
          setTypedPlaceholder(phrase.slice(0, charIndex));
        });
        if (charIndex > 0) {
          deleteTimer = window.setTimeout(deleteStep, DELETE_DELAY);
        } else {
          setPlaceholderIndex((i) => (i + 1) % CYCLING_PLACEHOLDERS.length);
        }
      };
      deleteStep();
    };

    const startTimer = window.setTimeout(typeStep, START_DELAY);
    return () => {
      cancelled = true;
      window.clearTimeout(startTimer);
      if (typeTimer) {
        window.clearTimeout(typeTimer);
      }
      if (dwellTimer) {
        window.clearTimeout(dwellTimer);
      }
      if (deleteTimer) {
        window.clearTimeout(deleteTimer);
      }
    };
  }, [placeholderIndex]);

  const chatPlaceholderStyle = useMemo(() => {
    return {
      ["--chat-placeholder"]: JSON.stringify(typedPlaceholder),
    } as unknown as React.CSSProperties;
  }, [typedPlaceholder]);

  const handleQuickPrompt = useQuickPrompt({
    editor: homeEditor,
    onSelectUploadImage: () => {
      void messageInputRef.current?.triggerFileUpload();
    },
    onSelectProduct: () => {
      void messageInputRef.current?.triggerContextMenu();
    },
  });

  const selectedCategoryChip = (
    <AnimatePresence>
      {selectedCategoryConfig && (
        <div className="flex items-center gap-1.5">
          {/* Parent chip */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1.5 px-3 py-2 h-8 border border-component-border rounded-md text-sm font-normal text-default bg-sheet"
          >
            {React.cloneElement(selectedCategoryConfig.icon, {
              className: "w-3.5 h-3.5",
            })}
            <span>{selectedCategoryConfig.label}</span>
            <Button
              icon
              variant="tertiary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleClearCategory();
              }}
              aria-label="Clear category"
              UNSAFE_className="ml-0.5 size-4 p-0"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </motion.div>

          {/* Child chip: Selected industry */}
          {selectedIndustry && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-1.5 px-3 py-2 h-8 border border-component-border rounded-md text-sm font-normal text-default bg-sheet"
            >
              {React.cloneElement(INDUSTRY_ICONS[selectedIndustry], {
                className: "w-3.5 h-3.5",
              })}
              <span>{selectedIndustry}</span>
              <Button
                icon
                variant="tertiary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBackFromIndustryPrompts();
                }}
                aria-label="Clear industry"
                UNSAFE_className="ml-0.5 size-4 p-0"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </motion.div>
          )}
        </div>
      )}
    </AnimatePresence>
  );

  return (
    // Inspiration for chat glow animation here: https://ui.aceternity.com/components/background-gradient
    <div className="flex flex-col w-full items-center justify-center min-w-0 gap-8 md:gap-12">
      <style>
        {`
          .home-chat-input.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-neutral-500);
            content: var(--chat-placeholder, "Ask Replo anything...") " ";
            float: left;
            height: 0;
            pointer-events: none;
            white-space: pre;
            margin-right: 0.35ch;
          }

          .home-chat-input.tiptap * {
            max-width: 100%;
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .home-chat-input.tiptap p {
            white-space: pre-wrap;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          .no-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .no-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          .home-chat-shell {
            position: relative;
            isolation: isolate;
            -webkit-tap-highlight-color: transparent;
          }

          /* Soft animated gradient glow behind the input (blurred layer) */
          .home-chat-shell::before {
            content: '';
            position: absolute;
            inset: -12px;
            border-radius: 24px;
            pointer-events: none;
            z-index: 0;
            opacity: 0.35;
            filter: blur(20px);
            background: radial-gradient(circle farthest-side at 0% 100%, rgba(255,160,40,0.10), transparent 75%),
                        radial-gradient(circle farthest-side at 100% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 100% 100%, rgba(255,70,60,0.10), transparent 80%),
                        radial-gradient(circle farthest-side at 0% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 50% 50%, rgba(255,130,85,0.10), transparent 85%);
            background-blend-mode: screen;
            background-size: 200% 200%;
            animation: aceternity-gradient 6s ease infinite;
            transition: opacity 400ms ease;
          }

          @media (hover: hover) {
            .home-chat-shell:hover::before {
              opacity: 0.5;
            }
          }

          .home-chat-shell:focus-within::before {
            opacity: 0.5;
          }

          /* Gradient border */
          .home-chat-border {
            position: absolute;
            inset: -4px;
            border-radius: 20px;
            z-index: 0;
            background: radial-gradient(circle farthest-side at 0% 100%, rgba(255,160,40,0.10), transparent 75%),
                        radial-gradient(circle farthest-side at 100% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 100% 100%, rgba(255,70,60,0.10), transparent 80%),
                        radial-gradient(circle farthest-side at 0% 0%, rgba(79,70,245,0.10), transparent 78%),
                        radial-gradient(circle farthest-side at 50% 50%, rgba(255,130,85,0.10), transparent 85%);
            background-blend-mode: screen;
            background-size: 200% 200%;
            animation: aceternity-gradient 6s ease infinite;
            opacity: 0.35;
          }

          @media (hover: hover) {
            .home-chat-shell:hover .home-chat-border {
              opacity: 0.5;
            }
          }

          .home-chat-shell:focus-within .home-chat-border {
            opacity: 0.5;
          }

          /* Thin 0.5px gradient outline around the outer glow border */
          .home-chat-shell::after {
            content: '';
            position: absolute;
            inset: -4.5px;
            border-radius: 20.5px;
            padding: 0.5px;
            pointer-events: none;
            z-index: 0;
            background: var(--color-gradient-ai-30);
            -webkit-mask:
              linear-gradient(#fff 0 0) content-box,
              linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask:
              linear-gradient(#fff 0 0) content-box,
              linear-gradient(#fff 0 0);
            mask-composite: exclude;
          }

          /* Form sits above the gradient border */
          .home-chat-shell form {
            position: relative;
            z-index: 1;
          }

          /* MessageInput styling - target the container inside Popover */
          .home-chat-shell form div.bg-white {
            border: none !important;
            background: white !important;
            border-radius: 16px !important;
            box-shadow: none !important;
          }


          @keyframes aceternity-gradient {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            .home-chat-shell::before {
              animation: none;
            }
          }

          /* Disable animations and transitions on touch devices to prevent flickering */
          @media (pointer: coarse) {
            .home-chat-shell::before {
              animation: none;
              transition: none;
              background-size: 100% 100%;
            }

            .home-chat-border {
              animation: none;
            }

            .home-chat-shell:focus-within::before,
            .home-chat-shell:focus-within .home-chat-border {
              opacity: 0.5;
            }
          }
        `}
      </style>

      <div className="flex items-center gap-3">
        <div className="font-semibold text-default cursor-default text-center text-xl md:text-3xl leading-tight">
          {heroText}
        </div>
        <Tag variant="beta" size="base">
          Beta
        </Tag>
      </div>

      <div className="flex flex-col w-full items-center justify-center min-w-0">
        <div className="relative flex flex-col gap-6 w-full md:px-6 max-w-full md:max-w-[850px]">
          <PaymentFailedBanner />

          <div className="relative">
            <TutorialAnchorWrapper
              anchorId="homepage-chat"
              side="top"
              sideOffset={24}
              align="center"
            >
              <div className="home-chat-shell">
                <div className="home-chat-border" />
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit();
                  }}
                  className="w-full cursor-text"
                  style={chatPlaceholderStyle}
                >
                  <MessageInput
                    ref={messageInputRef}
                    isChatLoading={false}
                    editor={homeEditor}
                    allowAttachments={true}
                    files={files}
                    setFiles={setFiles}
                    editorClassName="min-h-24 max-h-48 text-sm"
                    leftEnhancer={selectedCategoryChip}
                  />
                </form>
                {/* Integration strip — attached to bottom of chat input */}
                {shouldShowConnectToolsBanner ? (
                  <div
                    role="button"
                    tabIndex={0}
                    className="relative z-[1] flex items-center justify-between px-4 py-2.5 cursor-pointer transition-colors rounded-b-2xl"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(
                        generatePath(routes.shops.integrations.list, {
                          projectId: projectId ?? "",
                        }),
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(
                          generatePath(routes.shops.integrations.list, {
                            projectId: projectId ?? "",
                          }),
                        );
                      }
                    }}
                  >
                    <span
                      className="text-sm"
                      style={{
                        backgroundImage: "var(--color-gradient-ai-100)",
                        backgroundSize: "200% 100%",
                        backgroundPosition: "80% 0%",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      Connect your tools to Replo
                    </span>
                    <div className="flex items-center -space-x-2">
                      {INTEGRATION_BANNER_LOGOS.map((config) => (
                        <img
                          key={config.value}
                          src={config.image}
                          alt={config.title}
                          className="size-6 rounded-full border-2 border-white object-contain bg-white"
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </TutorialAnchorWrapper>
          </div>

          {/* No category selected: show category chips */}
          {selectedCategory === null && (
            <div
              ref={categoryChipsRef}
              className="relative z-[1] flex gap-3 overflow-x-auto no-scrollbar justify-center"
            >
              {PROMPT_CATEGORIES.map((category, index) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                >
                  <ChipButton
                    onClick={() => handleCategoryChange(category.id)}
                    startEnhancer={React.cloneElement(category.icon, {
                      className: "w-3.5 h-3.5",
                    })}
                  >
                    {category.label}
                  </ChipButton>
                </motion.div>
              ))}
            </div>
          )}

          {/* Industry category selected but no industry yet: show industry chips with search */}
          {selectedCategory === "by-industry" && selectedIndustry === null && (
            <div className="relative z-[1]">
              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={handleBackFromIndustry}
                  className="flex items-center gap-1 text-sm text-muted hover:text-default transition-colors cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Select an industry</span>
                </button>
                <div className="relative w-48">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted" />
                  <input
                    type="text"
                    placeholder="Search"
                    value={industrySearch}
                    onChange={handleIndustrySearchChange}
                    className="w-full pl-8 pr-2.5 py-1.5 text-xs border-0.5 border-border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="relative group/scroll">
                {canScrollIndustryRight && (
                  <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none z-10" />
                )}
                {canScrollIndustryLeft && (
                  <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent pointer-events-none z-10" />
                )}
                <div
                  ref={industryChipsRef}
                  className="flex gap-2 overflow-x-auto no-scrollbar scroll-smooth"
                >
                  <AnimatePresence>
                    {filteredIndustries.map((industry) => (
                      <motion.div
                        key={industry}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8, width: 0 }}
                        transition={{ duration: 0.15 }}
                        className="flex-shrink-0"
                      >
                        <ChipButton
                          onClick={() => handleIndustrySelect(industry)}
                          startEnhancer={React.cloneElement(
                            INDUSTRY_ICONS[industry],
                            {
                              className: "w-3.5 h-3.5",
                            },
                          )}
                        >
                          {industry}
                        </ChipButton>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                <Button
                  icon
                  variant="secondary"
                  size="base"
                  onClick={() => scrollIndustry("left")}
                  UNSAFE_className={twMerge(
                    "absolute -left-8 top-1/2 -translate-y-1/2",
                    "shadow-md z-10",
                    "hidden md:flex",
                    !canScrollIndustryLeft && "md:hidden",
                  )}
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-4 h-4 text-muted" />
                </Button>
                <Button
                  icon
                  variant="secondary"
                  size="base"
                  onClick={() => scrollIndustry("right")}
                  UNSAFE_className={twMerge(
                    "absolute -right-8 top-1/2 -translate-y-1/2",
                    "shadow-md z-10",
                    "hidden md:flex",
                    !canScrollIndustryRight && "md:hidden",
                  )}
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-4 h-4 text-muted" />
                </Button>
              </div>
            </div>
          )}

          {/* Industry AND specific industry selected: show industry-specific prompts */}
          {selectedCategory === "by-industry" && selectedIndustry !== null && (
            <div className="relative z-[1]">
              <HorizontalCarousel
                listenerDelay={350}
                gap="3"
                UNSAFE_className="pb-2"
              >
                {INDUSTRY_PROMPTS[selectedIndustry].map((prompt, index) => (
                  <motion.button
                    key={prompt.title}
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    onClick={() => {
                      logEvent("chat.selectRecommendationPrompt", {
                        origin: "home",
                        prompt: prompt.prompt,
                        category: `by-industry:${selectedIndustry}`,
                      });
                      handleQuickPrompt(
                        prompt.prompt,
                        prompt.action === "upload" ? "upload" : undefined,
                      );
                    }}
                    className="w-48 md:w-[calc((100%-24px)/3)] bg-white border-0.5 border-border rounded-xl p-3 md:p-4 flex flex-col gap-2 items-start text-left hover:bg-sheet transition-colors flex-none snap-start cursor-pointer"
                  >
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-sheet border border-border rounded-md flex items-center justify-center text-default">
                      {React.cloneElement(prompt.icon, {
                        className: "w-3 h-3 md:w-4 md:h-4",
                      })}
                    </div>
                    <div className="flex flex-col gap-0.5 w-full">
                      <div className="text-xs md:text-sm header-h6">
                        {prompt.title}
                      </div>
                      <div className="text-xs md:text-sm text-muted">
                        {prompt.description}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </HorizontalCarousel>
            </div>
          )}

          {/* Other category selected: show prompt cards */}
          {selectedCategory !== null && selectedCategory !== "by-industry" && (
            <div className="relative z-[1]">
              <HorizontalCarousel
                listenerDelay={350}
                gap="3"
                UNSAFE_className="pb-2"
              >
                {currentPrompts.map((prompt, index) => (
                  <motion.button
                    key={prompt.title}
                    initial={{ opacity: 0, scale: 0.9, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                    onClick={() => {
                      logEvent("chat.selectRecommendationPrompt", {
                        origin: "home",
                        prompt: prompt.prompt,
                        category: selectedCategory,
                      });
                      handleQuickPrompt(
                        prompt.prompt,
                        prompt.action === "upload" ? "upload" : undefined,
                      );
                    }}
                    className="w-48 md:w-[calc((100%-24px)/3)] bg-white border-0.5 border-border rounded-xl p-3 md:p-4 flex flex-col gap-2 items-start text-left hover:bg-sheet transition-colors flex-none snap-start cursor-pointer"
                  >
                    <div className="w-6 h-6 md:w-8 md:h-8 bg-sheet border border-border rounded-md flex items-center justify-center text-default">
                      {React.cloneElement(prompt.icon, {
                        className: "w-3 h-3 md:w-4 md:h-4",
                      })}
                    </div>
                    <div className="flex flex-col gap-0.5 w-full">
                      <div className="text-xs md:text-sm header-h6">
                        {prompt.title}
                      </div>
                      <div className="text-xs md:text-sm text-muted">
                        {prompt.description}
                      </div>
                    </div>
                  </motion.button>
                ))}
              </HorizontalCarousel>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
